import React, { useCallback, useEffect, useState, useMemo } from "react";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { BASE_URL } from "../api";
import { getApiHeaders, formatDate } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";

export default function Notification() {
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const [actionLoadingItemId, setActionLoadingItemId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [filterType, setFilterType] = useState("all"); // "all" | "unread" | "read"
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const recordsPerPage = 10;

  // Fetch Notifications List
  const fetchNotifications = useCallback(async (showGlobalLoader = true) => {
    if (showGlobalLoader) setLoading(true);
    try {
      let res;
      try {
        res = await axios.get(`${BASE_URL}/profile/settings/notificationhistory`, {
          headers: getApiHeaders(),
        });
      } catch (err) {
        if (err?.response?.status === 404) {
          res = await axios.get(`${BASE_URL}/profile/settings/notifications`, {
            headers: getApiHeaders(),
          });
        } else {
          throw err;
        }
      }

      if (res?.data && (res.data.code === 200 || res.data.status === "success")) {
        const rawList = Array.isArray(res.data.data)
          ? res.data.data
          : Array.isArray(res.data.notifications)
            ? res.data.notifications
            : [];
        setNotifications(rawList);
      } else {
        setNotifications([]);
        if (res?.data?.message) {
          MessageToster("error", res.data.message);
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      if (showGlobalLoader) setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchNotifications(true);
  }, [fetchNotifications]);

  // Check if item is read
  const checkIsRead = (item) => {
    return (
      item?.isread === 1 ||
      item?.isread === true ||
      item?.isread === "1" ||
      item?.isread === "true"
    );
  };

  // Mark Single Notification as Read
  const markAsRead = async (id) => {
    if (!id) return;
    try {
      setActionLoadingItemId(id);
      let res;
      try {
        res = await axios.put(
          `${BASE_URL}/profile/settings/notification/mark?id=${id}`,
          { id },
          { headers: getApiHeaders() }
        );
      } catch (putErr) {
        if (putErr?.response?.status === 404) {
          res = await axios.put(
            `${BASE_URL}/profile/settings/notification/mark`,
            { id },
            { headers: getApiHeaders() }
          );
        } else {
          throw putErr;
        }
      }

      if (res?.data && (res.data.code === 200 || res.data.status === "success")) {
        MessageToster("success", res.data.message || "Notification marked as read");
        setNotifications((prev) =>
          prev.map((item) => (item.id === id ? { ...item, isread: 1 } : item))
        );
        await fetchNotifications(false);
      } else {
        MessageToster("error", res?.data?.message || "Failed to mark as read");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setActionLoadingItemId(null);
    }
  };

  // Mark All Notifications as Read
  const markAllAsRead = async () => {
    try {
      setLoading(true);
      const res = await axios.put(
        `${BASE_URL}/profile/settings/markallnotification`,
        {},
        { headers: getApiHeaders() }
      );

      if (res?.data && (res.data.code === 200 || res.data.status === "success")) {
        MessageToster("success", res.data.message || "All notifications marked as read");
        setNotifications((prev) => prev.map((item) => ({ ...item, isread: 1 })));
        await fetchNotifications(false);
      } else {
        MessageToster("error", res?.data?.message || "Failed to mark all as read");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Delete Single Notification
  const deleteNotification = async (id) => {
    try {
      setActionLoadingItemId(id);
      let res;
      try {
        res = await axios.put(
          `${BASE_URL}/profile/settings/notification?id=${id}`,
          { id },
          { headers: getApiHeaders() }
        );
      } catch (putErr) {
        if (putErr?.response?.status === 405 || putErr?.response?.status === 404) {
          res = await axios.delete(
            `${BASE_URL}/profile/settings/notification?id=${id}`,
            { headers: getApiHeaders() }
          );
        } else {
          throw putErr;
        }
      }

      if (res?.data && (res.data.code === 200 || res.data.status === "success")) {
        MessageToster("success", res.data.message || "Notification deleted");
        setNotifications((prev) => prev.filter((item) => item.id !== id));
        await fetchNotifications(false);
      } else {
        MessageToster("error", res?.data?.message || "Failed to delete notification");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setActionLoadingItemId(null);
    }
  };

  // Delete All Notifications
  const deleteAllNotifications = async () => {
    try {
      setLoading(true);
      let res;
      try {
        res = await axios.put(
          `${BASE_URL}/profile/settings/allnotification`,
          {},
          { headers: getApiHeaders() }
        );
      } catch (putErr) {
        if (putErr?.response?.status === 404) {
          res = await axios.put(
            `${BASE_URL}/profile/settings/notification`,
            { all: true },
            { headers: getApiHeaders() }
          );
        } else {
          throw putErr;
        }
      }

      if (res?.data && (res.data.code === 200 || res.data.status === "success")) {
        MessageToster("success", res.data.message || "All notifications deleted");
        setNotifications([]);
      } else {
        MessageToster("error", res?.data?.message || "Failed to delete all notifications");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (id = null) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId) {
      await deleteNotification(deleteTargetId);
    } else {
      await deleteAllNotifications();
    }
    setShowDeleteModal(false);
  };

  // Safe metadata parse
  const parseMetadata = (meta) => {
    if (!meta) return {};
    if (typeof meta === "object") return meta;
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  };

  // Filtered list
  const filteredNotifications = useMemo(() => {
    if (filterType === "unread") {
      return notifications.filter((item) => !checkIsRead(item));
    }
    if (filterType === "read") {
      return notifications.filter((item) => checkIsRead(item));
    }
    return notifications;
  }, [notifications, filterType]);

  const unreadCount = useMemo(() => {
    return notifications.filter((item) => !checkIsRead(item)).length;
  }, [notifications]);

  const readCount = useMemo(() => {
    return notifications.filter((item) => checkIsRead(item)).length;
  }, [notifications]);

  // Pagination Logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredNotifications.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredNotifications.length / recordsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <>
      <div id="layout-wrapper">
        {loading && <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid px-2 px-sm-3">
              {/* Header Title Row */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <h2 className="fw-bold text-dark mb-1 font-size-18 font-size-md-22">
                        Notifications
                      </h2>
                      <p className="text-muted font-size-12 font-size-md-13 mb-0">
                        Stay updated with real-time PAMM trading and account activity
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {unreadCount > 0 ? (
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2.5 py-1.5 rounded-pill font-size-11 fw-semibold">
                          <i className="bx bxs-circle font-size-8 me-1" /> {unreadCount} Unread
                        </span>
                      ) : (
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-2.5 py-1.5 rounded-pill font-size-11 fw-semibold">
                          <i className="bx bx-check-double font-size-13 me-1" /> All Caught Up
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-light btn-sm border bg-white d-inline-flex align-items-center justify-content-center shadow-sm"
                        style={{ width: "32px", height: "32px" }}
                        onClick={() => fetchNotifications(true)}
                        title="Refresh Notifications"
                        disabled={loading}
                      >
                        <i className={`mdi mdi-refresh font-size-15 text-dark ${loading ? "bx-spin" : ""}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Area: Direct card feed on mobile, cleanly nested on desktop */}
              <div className="row justify-content-center">
                <div className="col-12">
                  {/* Top Action Toolbar */}
                  <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-2.5 mb-3 bg-white p-2.5 p-sm-3 rounded-3 border shadow-sm">
                    {/* Filter Tabs */}
                    <div className="d-flex align-items-center gap-1 bg-light p-1 rounded-3 border w-100 w-sm-auto justify-content-between justify-content-sm-start">
                      <button
                        type="button"
                        className={`btn btn-sm rounded-2 font-size-11 font-size-sm-12 fw-semibold px-2.5 px-sm-3 flex-fill flex-sm-grow-0 border-0 transition-all ${
                          filterType === "all"
                            ? "bg-white text-primary shadow-sm"
                            : "text-muted bg-transparent"
                        }`}
                        onClick={() => {
                          setFilterType("all");
                          setCurrentPage(1);
                        }}
                      >
                        All ({notifications.length})
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm rounded-2 font-size-11 font-size-sm-12 fw-semibold px-2.5 px-sm-3 flex-fill flex-sm-grow-0 border-0 transition-all ${
                          filterType === "unread"
                            ? "bg-white text-primary shadow-sm"
                            : "text-muted bg-transparent"
                        }`}
                        onClick={() => {
                          setFilterType("unread");
                          setCurrentPage(1);
                        }}
                      >
                        Unread ({unreadCount})
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm rounded-2 font-size-11 font-size-sm-12 fw-semibold px-2.5 px-sm-3 flex-fill flex-sm-grow-0 border-0 transition-all ${
                          filterType === "read"
                            ? "bg-white text-primary shadow-sm"
                            : "text-muted bg-transparent"
                        }`}
                        onClick={() => {
                          setFilterType("read");
                          setCurrentPage(1);
                        }}
                      >
                        Read ({readCount})
                      </button>
                    </div>

                    {/* Quick Action Buttons */}
                    <div className="d-flex align-items-center gap-2 w-100 w-sm-auto justify-content-between justify-content-sm-end">
                      {/* Mark All as Read Button */}
                      <button
                        type="button"
                        className="btn btn-light btn-sm border bg-white px-2.5 px-sm-3 py-1.5 font-size-11 font-size-sm-12 fw-semibold d-inline-flex align-items-center justify-content-center gap-1 text-secondary shadow-none flex-fill flex-sm-grow-0"
                        onClick={markAllAsRead}
                        disabled={unreadCount === 0 || loading}
                        title={unreadCount === 0 ? "All caught up" : "Mark all as read"}
                      >
                        <i className="bx bx-check-double font-size-14 text-primary" />
                        <span>Mark All Read</span>
                      </button>

                      {/* Delete All Button */}
                      <button
                        type="button"
                        className="btn btn-soft-danger btn-sm px-2.5 px-sm-3 py-1.5 font-size-11 font-size-sm-12 fw-semibold d-inline-flex align-items-center justify-content-center gap-1 rounded-2 flex-fill flex-sm-grow-0"
                        onClick={() => openDeleteModal(null)}
                        disabled={notifications.length === 0 || loading}
                      >
                        <i className="bx bx-trash font-size-13" />
                        <span>Delete All</span>
                      </button>
                    </div>
                  </div>

                  {/* Notifications Feed */}
                  {currentRecords.length > 0 ? (
                    <div className="notification-list d-flex flex-column gap-2.5 mb-4">
                      {currentRecords.map((item, index) => {
                        const isRead = checkIsRead(item);
                        const meta = parseMetadata(item.metadata);

                        // Extract only message from metadata or item
                        const messageText =
                          meta.msg || meta.message || item.message || item.activity_subtype || "Notification update";

                        return (
                          <div
                            key={item.id || index}
                            className={`rounded-3 position-relative transition-all ${
                              isRead
                                ? "bg-white border text-secondary"
                                : "bg-white shadow-sm border border-primary-subtle"
                            }`}
                            style={{
                              padding: "14px 16px",
                              borderLeft: isRead
                                ? "4px solid #cbd5e1"
                                : "4px solid var(--color-primary)",
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            {/* Top Header Row: Title + Badges + Action Buttons */}
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-1.5">
                              {/* Left Title & Tags */}
                              <div className="d-flex align-items-center flex-wrap gap-2" style={{ minWidth: 0 }}>
                                <span className="font-size-13 font-size-sm-14 fw-bold text-dark text-break">
                                  {item.activity_subtype || item.activity_type || "Notification"}
                                </span>

                                {item.activity_type && (
                                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-0.5 rounded font-size-10 fw-semibold">
                                    {item.activity_type}
                                  </span>
                                )}

                                {!isRead && (
                                  <span className="badge bg-primary text-white rounded-pill font-size-9 px-2 py-0.5 fw-semibold">
                                    New
                                  </span>
                                )}
                              </div>

                              {/* Right Action Icons */}
                              <div className="d-flex align-items-center gap-1.5 flex-shrink-0">
                                {!isRead && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-light border bg-white text-primary rounded-circle d-flex align-items-center justify-content-center shadow-none p-0"
                                    style={{ width: "28px", height: "28px" }}
                                    onClick={() => markAsRead(item.id)}
                                    title="Mark as Read"
                                    disabled={actionLoadingItemId === item.id}
                                  >
                                    {actionLoadingItemId === item.id ? (
                                      <span
                                        className="spinner-border spinner-border-sm"
                                        style={{ width: "10px", height: "10px" }}
                                        role="status"
                                      />
                                    ) : (
                                      <i className="bx bx-check font-size-15" />
                                    )}
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="btn btn-sm btn-light border-0 text-muted rounded-circle d-flex align-items-center justify-content-center p-0"
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    transition: "all 0.2s ease",
                                  }}
                                  onClick={() => openDeleteModal(item.id)}
                                  title="Delete Notification"
                                  disabled={actionLoadingItemId === item.id}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#fee2e2";
                                    e.currentTarget.style.color = "#ef4444";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "";
                                    e.currentTarget.style.color = "";
                                  }}
                                >
                                  <i className="bx bx-trash font-size-14" />
                                </button>
                              </div>
                            </div>

                            {/* Timestamp Row */}
                            <div className="text-muted font-size-11 mb-2 d-flex align-items-center gap-1.5">
                              <i className="bx bx-time-five font-size-12 text-muted" />
                              <span style={{ whiteSpace: "nowrap" }}>
                                {item.ondate ? formatDate(item.ondate) : "Just now"}
                              </span>
                            </div>

                            {/* Message Sentence */}
                            <p
                              className={`mb-0 font-size-12 font-size-sm-13 ${
                                isRead ? "text-muted" : "text-dark"
                              }`}
                              style={{ lineHeight: 1.5 }}
                            >
                              {messageText}
                            </p>
                          </div>
                        );
                      })}

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3 border-top">
                          <span className="text-muted font-size-11 font-size-sm-12">
                            Showing {indexOfFirstRecord + 1} -{" "}
                            {Math.min(indexOfLastRecord, filteredNotifications.length)} of{" "}
                            {filteredNotifications.length}
                          </span>

                          <ul className="pagination pagination-sm mb-0 gap-1">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                              <button
                                type="button"
                                className="page-link rounded-2 border shadow-none"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                <i className="bx bx-chevron-left" />
                              </button>
                            </li>

                            {Array.from({ length: totalPages }, (_, idx) => {
                              const page = idx + 1;
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              ) {
                                const isActive = currentPage === page;
                                return (
                                  <li
                                    key={page}
                                    className={`page-item ${isActive ? "active" : ""}`}
                                  >
                                    <button
                                      type="button"
                                      className={`page-link rounded-2 border shadow-none ${
                                        isActive
                                          ? "bg-primary text-white border-primary"
                                          : "bg-white text-dark"
                                      }`}
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              } else if (page === currentPage - 2 || page === currentPage + 2) {
                                return (
                                  <li key={page} className="page-item disabled">
                                    <span className="page-link border-0">...</span>
                                  </li>
                                );
                              }
                              return null;
                            })}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                type="button"
                                className="page-link rounded-2 border shadow-none"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                <i className="bx bx-chevron-right" />
                              </button>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Empty State */
                    <div className="text-center py-5 my-3 bg-white rounded-3 border shadow-sm">
                      <div
                        className="avatar-lg rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3 shadow-sm"
                        style={{ width: "64px", height: "64px" }}
                      >
                        <i className="bx bx-bell-off font-size-28 text-muted opacity-75" />
                      </div>
                      <h6 className="fw-bold text-dark mb-1">No notifications found</h6>
                      <p className="text-muted font-size-12 mb-0">
                        {filterType === "unread"
                          ? "You have read all your notifications!"
                          : filterType === "read"
                            ? "No read notifications found."
                            : "You're all caught up! Check back later for new alerts."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ backdropFilter: "blur(4px)", zIndex: 1050 }}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered px-3">
              <div className="modal-content overflow-hidden">
                <div className="modal-header border-bottom-0 pb-0 pt-4 px-4 justify-content-between">
                  <h5 className="modal-title">
                    Confirm Deletion
                  </h5>
                  <button
                    type="button"
                    className="btn-close shadow-none"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={loading || actionLoadingItemId}
                  />
                </div>
                <div className="modal-body p-4 text-center">
                  <div
                    className="avatar-md rounded-circle bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "56px", height: "56px" }}
                  >
                    <i className="bx bx-trash font-size-24" />
                  </div>
                  <h5 className="fw-bold text-dark mb-2 font-size-15">Are you sure?</h5>
                  <p className="text-muted font-size-12 mb-4">
                    {deleteTargetId
                      ? "Do you really want to delete this notification? This action cannot be undone."
                      : "Do you really want to delete all notifications? This action cannot be undone."}
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-light px-3 py-1.5 font-size-12 fw-semibold border"
                      onClick={() => setShowDeleteModal(false)}
                      disabled={loading || actionLoadingItemId}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger px-3 py-1.5 font-size-12 fw-semibold shadow-sm"
                      onClick={handleConfirmDelete}
                      disabled={loading || actionLoadingItemId}
                    >
                      {loading || actionLoadingItemId ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1.5"
                            role="status"
                            aria-hidden="true"
                          />
                          Deleting...
                        </>
                      ) : (
                        "Yes, Delete"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
